import { toast } from "sonner";
import customFetch from "../util/config/axiosConfig";
import { ApiResponse } from "../types/responseType";
import {
  FormItem,
  FormListResponse,
  FormResponsePage,
} from "../types/formType";

const FormApis = {
  getFormList: async ({
    page = 0,
    size = 10,
    createdBy,
    createdOn,
    formName,
  }: {
    page?: number;
    size?: number;
    createdBy?: string;
    createdOn?: string;
    formName?: string;
  }): Promise<ApiResponse<FormListResponse> | undefined> => {
    try {
      const params = new URLSearchParams();

      params.append("page", String(page));
      params.append("size", String(size));

      if (createdBy) params.append("createdBy", createdBy);
      if (createdOn) params.append("createdOn", createdOn);
      if (formName) params.append("formName", formName);

      const response = await customFetch.get(
        `/api/v1/form/filter-forms?${params.toString()}`
      );

      return response.data;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load form list");
      return undefined;
    }
  },

  getFormBySeqId: async (
    seqId: string
  ): Promise<ApiResponse<FormItem> | undefined> => {
    try {
      const res = await customFetch.get(`/api/v1/form/get-form/${seqId}`);
      return res.data as ApiResponse<FormItem>;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load form");
      return undefined;
    }
  },

  createForm: async (
    payload: { formName: string },
    branchCode: string
  ): Promise<ApiResponse<FormItem> | undefined> => {
    try {
      const response = await customFetch.post(
        `/api/v1/form/create-form/${branchCode}`,
        payload
      );

      return response.data as ApiResponse<FormItem>;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Something went wrong");
      return undefined;
    }
  },

  //update form
  updateForm: async (formId: string, formName: string) => {
    try {
      const response = await customFetch.put(
        `/api/v1/form/update-form/${formId}`,
        { formName }
      );

      toast.success(response.data.data || "Form updated successfully");
      return response.data.data;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to update form");
      throw err;
    }
  },

  createSection: async (formId: string, sectionName: string) => {
    try {
      const response = await customFetch.post(
        `/api/v1/form/add-section/${formId}`,
        { sectionName: sectionName }
      );

      return response.data;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to create section");
      throw err;
    }
  },

  createQuestion: async (sectionId: string, questionData: any) => {
    try {
      const response = await customFetch.post(
        `/api/v1/form/add-question/${sectionId}`,
        questionData
      );

      return response.data; // should return the created question
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to add question");
      throw err;
    }
  },

  deleteSection: async (sectionId: string) => {
    try {
      const response = await customFetch.delete(
        `/api/v1/form/remove-section/${sectionId}`
      );

      return response.data;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete section");
      throw err;
    }
  },

  updateSection: async (sectionId: string, sectionName: string) => {
    try {
      const response = await customFetch.put(
        `/api/v1/form/update-section/${sectionId}`,
        { sectionName }
      );

      return response.data;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to update section");
      throw err;
    }
  },

  updateQuestion: async (questionId: string, questionData: any) => {
    try {
      const response = await customFetch.put(
        `/api/v1/form/update-question/${questionId}`,
        questionData
      );

      return response.data;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to update question");
      throw err;
    }
  },

  deleteForm: async (formId: number) => {
    try {
      const response = await customFetch.delete(`/api/v1/form/${formId}`);

      return response.data;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete section");
      throw err;
    }
  },

  deleteQuestion: async (questionId: number) => {
    try {
      const response = await customFetch.delete(
        `/api/v1/form/remove-question/${questionId}`
      );
      toast.success(response?.data?.data || "Question removed successfully");
      return response.data;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete section");
      throw err;
    }
  },

  submitFormResponse: async (payload: {
    formEntryId: number;
    formSeqId: string;
    answers: { questionId: number; answer: any }[];
  }) => {
    try {
      const res = await customFetch.post("/api/v1/form-response", payload);

      return res.data;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to submit form");
      throw err;
    }
  },

  getFormResponseByEntryId: async (
    formEntryId: number
  ): Promise<ApiResponse<any> | undefined> => {
    try {
      const res = await customFetch.get(`/api/v1/form-response/${formEntryId}`);
      return res.data;
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to load form response"
      );
      return undefined;
    }
  },

  getFormResponsesBySeqId: async (
    formSeqId: string,
    page = 0,
    size = 10
  ): Promise<ApiResponse<FormResponsePage> | undefined> => {
    try {
      const res = await customFetch.get(
        `/api/v1/form-response/filter/${formSeqId}?page=${page}&size=${size}`
      );
      return res.data;
    } catch (err: any) {
      toast.error("Failed to load form responses");
      return undefined;
    }
  },

  deleteFromResponse: async (formAnswerId : number) => {
    try {
      const response = await customFetch.delete(
        `/api/v1/form-response/${formAnswerId }`
      );
      toast.success(response?.data?.data || "Form response removed successfully");
      return response.data;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete form response");
      throw err;
    }
  },
};

export default FormApis;
